import { Box, IconButton, InputArea } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useCallback, useState, KeyboardEvent } from 'react';

import { RelatedQuestionsSlider } from './RelatedQuestionsSlider';
import { SiteSelectorButton } from './SiteSelectorButton';
import { SiteSelectorModal } from './SiteSelectorModal';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import { useChatbotMessageSender } from '../../../ArticleRightSideBar/useChatbotMessageSender';

import css from './index.module.scss';

type PinnedChatInputProps = {
  relatedQuestions?: string[];
  hidden?: boolean;
};

export const PinnedChatInput = ({
  relatedQuestions,
  hidden,
}: PinnedChatInputProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSiteSelectorModalOpen, setIsSiteSelectorModalOpen] = useState(false);
  const { sendMessage } = useChatbotMessageSender({});

  const handleSendMessage = (message?: string) => {
    const text = message ?? query;
    if (text.trim()) {
      sendMessage(text);
      setQuery('');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const handleOutsidePinnedChatClick = useCallback(() => {
    if (isSiteSelectorModalOpen) {
      return;
    }
    setIsFocused(false);
  }, [isSiteSelectorModalOpen]);
  const outsideClickRef = useOutsideClick(handleOutsidePinnedChatClick);

  const showRelatedQuestions =
    isFocused && relatedQuestions && relatedQuestions.length > 0;

  return (
    <Box className={classNames(css.wrapper, { [css.hidden]: hidden })} ref={outsideClickRef} direction="vertical">
      {showRelatedQuestions && (
        <RelatedQuestionsSlider
          relatedQuestions={relatedQuestions}
          onQuestionClick={handleSendMessage}
        />
      )}
      <div
        className={classNames(css.inputBox, { [css.focused]: isFocused })}
        onClick={() => !isFocused && setIsFocused(true)}
      >
        <InputArea
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          value={query}
          rows={isFocused ? 2 : 1}
          className={css.inputArea}
          size="medium"
          placeholder={t('article-page.copilot.input.placeholder')}
          autoFocus={isFocused}
        />
        <div className={css.inputFooter}>
          <SiteSelectorButton
            onOpenPicker={() => setIsSiteSelectorModalOpen(true)}
          />
          <IconButton
            size="medium"
            className={css.sendButton}
            skin="ai"
            disabled={!query.trim()}
            onClick={() => handleSendMessage()}
          >
            <ArrowRight />
          </IconButton>
        </div>
        {!isFocused && (
          <IconButton
            size="small"
            className={css.sendButton}
            skin="ai"
            disabled
            onClick={() => handleSendMessage()}
          >
            <ArrowRight />
          </IconButton>
        )}
      </div>
      <SiteSelectorModal
        isOpen={isSiteSelectorModalOpen}
        onClose={() => setIsSiteSelectorModalOpen(false)}
        isLoadingSites={false}
      />
    </Box>
  );
};
